/**
 * Retry configuration options
 */
interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Default configuration for retry logic
 */
const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffFactor: 2,
  shouldRetry: (error) => {
    // Retry on network errors or 5xx server errors
    if (error instanceof Error) {
      // Network errors
      if (error.message.includes('fetch')) return true;
      
      // Check for HTTP status codes
      if ('status' in error && typeof error.status === 'number') {
        const status = error.status;
        // Retry on server errors (5xx) or rate limiting (429)
        return status >= 500 || status === 429;
      }
    }
    return false;
  }
};

/**
 * Execute a function with exponential backoff retry logic
 * @param fn The async function to execute
 * @param options Retry configuration options
 * @returns Promise resolving to the function result
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Check if we should retry
      if (attempt === config.maxRetries || !config.shouldRetry(error)) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        config.initialDelay * Math.pow(config.backoffFactor, attempt),
        config.maxDelay
      );
      
      // Add jitter to prevent thundering herd
      const jitter = delay * 0.1 * Math.random();
      const totalDelay = delay + jitter;
      
      console.log(`Retry attempt ${attempt + 1}/${config.maxRetries} after ${Math.round(totalDelay)}ms`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, totalDelay));
    }
  }
  
  throw lastError;
}

/**
 * Create a retry wrapper with custom configuration
 * @param options Custom retry options
 * @returns A retry function with the specified configuration
 */
export function createRetryWrapper(options: RetryOptions = {}) {
  return <T>(fn: () => Promise<T>) => retryWithBackoff(fn, options);
}